import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { AuthEmailActionsService } from '@jfw/angular/ecommerce/core';
import { JfwEmailValidator } from '@jfw/angular/shared/common-components/form-fields';
import { Site } from '@jfw/types';
import { catchError, of, Subject, switchMap, tap } from 'rxjs';

type ResetPasswordForm = {
  email: FormControl<string>;
};

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard,
    RouterLink,
    MatButton,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatProgressSpinner,
  ],
})
export class ResetPasswordComponent {
  private authEmailActionService = inject(AuthEmailActionsService);

  public spinner = signal(false);

  public form = new FormGroup<ResetPasswordForm>({
    email: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        JfwEmailValidator.isValid(),
        Validators.maxLength(150),
      ],
    }),
  });

  private resetPasswordRequest$ = new Subject<string | null>();

  public onSubmit(): void {
    if (this.form.invalid) return;
    const email = this.form.controls.email.value;
    this.resetPasswordRequest$.next(email);
    this.spinner.set(true);
  }

  public onRetry(): void {
    this.resetPasswordRequest$.next(null);
  }

  private resetPasswordResponse$ = this.resetPasswordRequest$.pipe(
    switchMap((email) => {
      if (email) {
        return this.authEmailActionService
          .sendResetPassword(email, Site.DealerPortal)
          .pipe(
            tap(() => this.spinner.set(false)),
            catchError((error) => {
              console.error(error);
              this.spinner.set(false);
              return of({ success: false });
            }),
          );
      } else {
        return of(null);
      }
    }),
  );

  public resetPasswordResponse = toSignal(this.resetPasswordResponse$);
}
